import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

type EmailProvider = 'alibaba' | 'postmark';
type EmailProviderFallback = 'none' | EmailProvider;

interface PlatformEmailConfig {
  primaryProvider: EmailProvider;
  fallbackProvider: EmailProviderFallback;
  providers: Record<EmailProvider, { isConfigured: boolean }>;
}

export const EmailPage = () => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [primaryProvider, setPrimaryProvider] = useState<EmailProvider>('alibaba');
  const [fallbackProvider, setFallbackProvider] = useState<EmailProviderFallback>('none');

  const email = useQuery({
    queryKey: ['platform-email'],
    queryFn: () => api.get<PlatformEmailConfig>('/platform/email'),
  });

  useEffect(() => {
    if (!email.data) return;
    setPrimaryProvider(email.data.primaryProvider);
    setFallbackProvider(email.data.fallbackProvider);
  }, [email.data]);

  const save = useMutation({
    mutationFn: () => api.patch('/platform/email', { primaryProvider, fallbackProvider }),
    onSuccess: async () => {
      toast.success(t('email.saved'));
      await queryClient.invalidateQueries({ queryKey: ['platform-email'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const fallbackHelp =
    fallbackProvider === 'none'
      ? t('email.fallbackHelp.none')
      : fallbackProvider === primaryProvider
        ? t('email.fallbackHelp.same')
        : t('email.fallbackHelp.other');

  return (
    <div>
      <PageHeader title={t('email.title')} description={t('email.description')} />
      <ReadOnlyNotice />

      <Card>
        <CardHeader>
          <CardTitle>{t('email.routingTitle')}</CardTitle>
          <CardDescription>{t('email.routingDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            {(['alibaba', 'postmark'] as const).map((provider) => (
              <div key={provider} className="flex items-center justify-between gap-2">
                <span className="text-body-sm font-medium">{t(`email.providers.${provider}`)}</span>
                <Badge
                  variant={email.data?.providers[provider].isConfigured ? 'success' : 'warning'}
                >
                  {email.data?.providers[provider].isConfigured
                    ? t('email.configured')
                    : t('email.notConfigured')}
                </Badge>
              </div>
            ))}
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            <FormField label={t('email.primary')}>
              {(field) => (
                <Listbox
                  {...field}
                  value={primaryProvider}
                  onChange={(value) => value && setPrimaryProvider(value)}
                  options={[
                    { value: 'alibaba', label: t('email.providers.alibaba') },
                    { value: 'postmark', label: t('email.providers.postmark') },
                  ]}
                />
              )}
            </FormField>
            <FormField label={t('email.fallback')} hint={fallbackHelp}>
              {(field) => (
                <Listbox
                  {...field}
                  value={fallbackProvider}
                  onChange={(value) => value && setFallbackProvider(value)}
                  options={[
                    { value: 'none', label: t('email.providers.none') },
                    { value: 'alibaba', label: t('email.providers.alibaba') },
                    { value: 'postmark', label: t('email.providers.postmark') },
                  ]}
                />
              )}
            </FormField>
          </div>

          {isAdmin ? (
            <div className="flex justify-end">
              <Button loading={save.isPending} disabled={email.isLoading} onClick={() => save.mutate()}>
                {t('email.save')}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
